import {DateTime, Interval} from "luxon";
import Delta from "quill-delta";
import {container} from "tsyringe";
import type {RteValue} from "../rte-value.type";
import {RteValueHelper} from "../../helpers/rte-value.helper";
import {DateHelper} from "../../helpers/date.helper";
import {IssueRepository} from "../repositories/issue.repository";
import {EmployeeToIssueAssignmentRepository} from "../repositories/employee-to-issue-assignment.repository";
import {Employee} from "./employee.entity";
import {Entity} from "./entity";
import {IssueStatus} from "./issue-status.entity";
import {IssueType} from "./issue-type.entity";
import {Keyword} from "./keyword.entity";
import {Project} from "./project.entity";
import {EmployeeToIssueAssignment} from "./employee-to-issue-assignment.entity";
import {viewComponent} from "../../decorators/view-component.decorator";
import {typeInfo} from "../../decorators/type-info.decorator";

export class Issue extends Entity<Issue> {
    /**
     * Creates an issue of the given type. The status may be null: issues of a statusless type
     * (absence, public holiday, reminder) lose their status in the issue-type data migration.
     */
    public constructor(type: IssueType, status: IssueStatus | null) {
        super();

        this._type = type;
        this._setOriginalValue(`type`, type);

        this._status = status;
        this._setOriginalValue(`status`, status);
    }

    private _creator: Employee | null = null;

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: true, type: () => Employee})
    @viewComponent("ha-entity-viewer")
    public get creator(): Employee | null {
        return this._creator;
    }

    public set creator(value: Employee | null) {
        this._creator = value;
        this._setOriginalValue(`creator`, value);
    }

    private _deletedAt: DateTime | null = null;

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => DateTime})
    @viewComponent("ha-date-viewer")
    public get deletedAt(): DateTime | null {
        return this._deletedAt;
    }

    public set deletedAt(value: DateTime | null) {
        this._deletedAt = value;
    }

    // A new issue starts as an empty DOCUMENT, not an empty Quill delta. RteValueHelper.empty()
    // exists for exactly this and says so in its own comment ("a just-added LinkTile, a new
    // Issue") — an empty delta here would be converted straight back on the first render.
    private _description: RteValue = RteValueHelper.empty();

    /**
     * `RteValue` (`Delta | RichTextDocument`) since the RTE unification
     * (2026-09): a row not yet re-saved is still the legacy Quill `Delta` it
     * was written as; every save from `ha-rte` on writes a `RichTextDocument`.
     * Both shapes are accepted here as-is (no forced Delta coercion any
     * more, unlike before this migration) — `ha-rte`/`ha-rte-viewer` are the
     * ones that normalize a Delta into a `RichTextDocument`, lazily, on
     * render (`RteValueHelper.toRichTextDocument`).
     *
     * Review-Lauf 01 (MINOR, Linse A/B): `@typeInfo`'s `type` is `Object`, not
     * `Delta` — the actual runtime value is `RteValue` (either shape), and
     * `Delta` would be actively wrong for a migrated row. Currently
     * consumed only for `isEntity: true` fields (`ha-entity-viewer.component.ts`),
     * so this field's own `type` is not read anywhere today — kept honest
     * regardless, against a future generic consumer (audit-log diffing,
     * reflection-based clone/compare) that might start reading it.
     */
    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => Object})
    @viewComponent("ha-rte-viewer")
    public get description(): RteValue {
        return this._description;
    }

    public set description(value: RteValue) {
        if (RteValueHelper.isQuillDelta(value) && !(value instanceof Delta)) {
            value = new Delta((value as { ops: unknown[] }).ops as never);
        }
        this._description = value;
        this._setOriginalValue(`description`, value);
    }

    private _employees: Employee[] = [];

    @typeInfo({isEnumerable: true, isPromise: false, isEntity: true, type: () => Employee})
    @viewComponent("ha-entity-viewer")
    public get employees(): Employee[] {
        return this._employees;
    }

    public set employees(value: Employee[]) {
        this._employees = value;
        this._setOriginalValue(`employees`, value);
    }

    /**
     * Sets the employee list derived from the assignments, without change tracking.
     *
     * `employees` is not user input but derived from `employeeToIssueAssignments`, which
     * the dialog saves. Through the tracked setter it would count as changed on an issue
     * loaded from the server and travel with its next `update.issue`. Like the field
     * initializer, this emits no entity events and does not bump the change tick (read
     * only by the dialogs' re-entry guards).
     */
    public applyDerivedEmployees(employees: Employee[]): void {
        this._employees = employees;
    }

    private _interval: Interval = Interval.fromDateTimes(DateTime.fromSeconds(0, {zone: `utc`}), DateHelper.fromSQL(`2100-01-01 00:00:00.000`));

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => Interval})
    @viewComponent("ha-interval-viewer")
    public get interval(): Interval {
        return this._interval;
    }

    private _keywords: Keyword[] = [];

    @typeInfo({isEnumerable: true, isPromise: false, isEntity: true, type: () => Keyword})
    @viewComponent("ha-entity-viewer")
    public get keywords(): Keyword[] {
        return this._keywords;
    }

    public set keywords(value: Keyword[]) {
        this._keywords = value;
        this._setOriginalValue(`keywords`, value);
    }

    private _project: Project | null = null;

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: true, type: () => Project})
    @viewComponent("ha-entity-viewer")
    public get project(): Project | null {
        return this._project;
    }

    public set project(value: Project | null) {
        if (value) {
            value.addIssue(this);
        }
        this._project = value;
        this._setOriginalValue(`project`, value);
    }

    private _status: IssueStatus | null;

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: true, type: () => IssueStatus})
    @viewComponent("ha-entity-viewer")
    public get status(): IssueStatus | null {
        return this._status;
    }

    public set status(value: IssueStatus | null) {
        this._status = value;
        this._setOriginalValue(`status`, value);
    }

    private _title: string = ``;

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => String})
    @viewComponent("ha-string-viewer")
    public get title(): string {
        return this._title;
    }

    public set title(value: string) {
        this._title = value;
        this._setOriginalValue(`title`, value);
    }

    private _type: IssueType;

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: true, type: () => IssueType})
    @viewComponent("ha-entity-viewer")
    public get type(): IssueType {
        return this._type;
    }

    public set type(value: IssueType) {
        this._type = value;
        this._setOriginalValue(`type`, value);
    }

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => DateTime})
    @viewComponent("ha-date-viewer", {
        preRender: (value: DateTime) => value.minus({days: 1})
    })
    public get end(): DateTime {
        return this._interval.end!;
    }

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => Boolean})
    @viewComponent("ha-boolean-viewer")
    public get isDeleted(): boolean {
        return this._deletedAt instanceof DateTime;
    }

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => DateTime})
    @viewComponent("ha-date-viewer")
    public get start(): DateTime {
        return this._interval.start!;
    }

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => Interval})
    @viewComponent("ha-interval-viewer")
    public get timeRange(): Interval {
        return this._interval;
    }

    public set timeRange(value: Interval) {
        if (this._interval.equals(value)) {
            return;
        }
        this._interval = value;
        this._setOriginalValue(`timeRange`, value);
    }

    private _employeeToIssueAssignments: EmployeeToIssueAssignment[] = [];

    @typeInfo({isEnumerable: true, isPromise: false, isEntity: true, type: () => EmployeeToIssueAssignment})
    @viewComponent("ha-entity-viewer")
    public get employeeToIssueAssignments(): EmployeeToIssueAssignment[] {
        return this._employeeToIssueAssignments;
    }

    public set employeeToIssueAssignments(value: EmployeeToIssueAssignment[]) {
        this._employeeToIssueAssignments = value;
        this._setOriginalValue(`employeeToIssueAssignments`, value);
    }

    public async clone(): Promise<Issue> {
        const clonedIssue = await container.resolve(IssueRepository).create();

        clonedIssue._creator = this._creator;
        clonedIssue._deletedAt = this._deletedAt;
        // A deep copy in the same shape, so editing the clone cannot change the original
        // (see RteValueHelper.copy for why the Delta branch copies its ops array as well).
        clonedIssue._description = RteValueHelper.copy(this._description);
        clonedIssue._employees = [...this._employees];
        clonedIssue._interval = Interval.fromDateTimes(this._interval.start!, this._interval.end!);
        clonedIssue._keywords = [...this._keywords];
        clonedIssue._project = this._project;
        clonedIssue._title = this._title;
        clonedIssue._type = this._type;
        // A clone starts as new work: OPEN (from create()) when its type carries a status, none when
        // the type is statusless.
        if (this._type.isStatusless) {
            clonedIssue._status = null;
        }

        // Not a plain reference copy, deliberately: each EmployeeToIssueAssignment
        // carries an `.issue` back-reference (by uid) to the specific Issue it
        // belongs to. Copying the original assignment objects would leave the clone's
        // assignments still pointing at the ORIGINAL issue's uid — wrong on save, and
        // also the exact reason a plain copy here silently broke every "pre-fill the
        // assignee" caller of clone() (AbstractEntitiesDialogComponent.createNewEntity()
        // runs this automatically for every creationMode dialog): assignment.employee
        // resolves fine on the original, but the clone needs its OWN assignment rows.
        const assignmentRepo = container.resolve(EmployeeToIssueAssignmentRepository);
        const clonedAssignments = await Promise.all(
            this._employeeToIssueAssignments.map(async assignment => {
                const employee = await assignment.employee;
                return assignmentRepo.create(clonedIssue, employee);
            })
        );
        clonedIssue._employeeToIssueAssignments = clonedAssignments.filter(
            (assignment): assignment is EmployeeToIssueAssignment => !!assignment
        );

        return clonedIssue;
    }

    public toString(): string {
        if (this.title.length > 0) {
            return this.title;
        }

        return `${!!this.project ? `${this.project.title}:` : ``} ${this.type.toString()}`.trim();
    }
}